import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecords, deleteRecord, clearAllRecords, formatRecordDate } from '@/utils/divinationStorage';
import type { DivinationRecord } from '@/utils/divinationStorage';
import { TRIGRAM_MAP, getHexagram } from '@/mocks/hexagrams';

export default function DivinationHistory() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<DivinationRecord[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const loadRecords = useCallback(() => {
    setRecords(getRecords());
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleDelete = useCallback((id: string) => {
    deleteRecord(id);
    loadRecords();
  }, [loadRecords]);

  const handleClearAll = useCallback(() => {
    clearAllRecords();
    setRecords([]);
    setShowClearConfirm(false);
  }, []);

  const handleViewDetail = useCallback((record: DivinationRecord) => {
    const lowerTrigram = record.lowerTrigram;
    const upperTrigram = record.upperTrigram;
    const hexagram = getHexagram(upperTrigram, lowerTrigram);

    navigate('/interpretation', {
      state: {
        question: record.question,
        rounds: record.rounds.map((r, i) => ({
          round: r.round || i + 1,
          results: r.results,
          coinValues: r.results.map((res) => res === '正面' ? 3 : 2),
          total: r.total,
        })),
        lowerTrigram,
        upperTrigram,
        hexagram,
        interpretation: record.interpretation,
        recordId: record.id,
      },
    });
  }, [navigate]);

  if (records.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 animate-fade-in">
        <div className="w-16 h-16 flex items-center justify-center mb-4 bg-background-100 rounded-full border border-background-200">
          <i className="ri-history-line text-3xl text-foreground-300"></i>
        </div>
        <p className="text-sm text-foreground-500 mb-1">暂无问卜记录</p>
        <p className="text-xs text-foreground-400">完成一次问卜后，记录会自动保存在这里</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-foreground-400">共 {records.length} 条记录</p>
        <button
          onClick={() => setShowClearConfirm(true)}
          className="text-xs text-foreground-400 hover:text-accent-600 cursor-pointer whitespace-nowrap flex items-center gap-1 transition-colors"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-delete-bin-line text-sm"></i>
          </div>
          清空记录
        </button>
      </div>

      {/* 清空确认弹窗 */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-6">
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-xs">
            <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 text-center">确认清空？</h3>
            <p className="text-sm text-foreground-500 text-center mb-5">所有问卜记录将被永久删除，无法恢复。</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-2.5 rounded-xl bg-background-200 text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleClearAll}
                className="flex-1 py-2.5 rounded-xl bg-accent-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {records.map((record) => {
          const lowerInfo = TRIGRAM_MAP[record.lowerTrigram];
          const upperInfo = TRIGRAM_MAP[record.upperTrigram];
          const isExpanded = expandedId === record.id;

          return (
            <div
              key={record.id}
              className="bg-background-100 rounded-xl border border-background-200 overflow-hidden"
            >
              {/* 记录头部 */}
              <div
                className="flex items-start gap-3 p-4 cursor-pointer"
                onClick={() => setExpandedId(isExpanded ? null : record.id)}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary-100 flex flex-col items-center justify-center border border-primary-200">
                  <span className="text-base leading-none">{lowerInfo?.symbol || '?'}</span>
                  <span className="text-base leading-none mt-0.5">{upperInfo?.symbol || '?'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading text-sm font-bold text-foreground-900">{record.hexagramName}</span>
                    <span className="text-xs text-foreground-400">第{record.hexagramNumber}卦</span>
                  </div>
                  <p className="text-xs text-foreground-600 truncate mb-1">
                    问：{record.question || '（未输入问题）'}
                  </p>
                  <p className="text-xs text-foreground-400">{formatRecordDate(record.createdAt)}</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1">
                  <div className="w-5 h-5 flex items-center justify-center text-foreground-400">
                    {isExpanded ? <i className="ri-arrow-up-s-line text-base"></i> : <i className="ri-arrow-down-s-line text-base"></i>}
                  </div>
                </div>
              </div>

              {/* 展开详情 */}
              {isExpanded && (
                <div className="border-t border-background-200 px-4 pb-4 pt-3 space-y-3 animate-fade-in">
                  {/* 六爻记录 */}
                  <div>
                    <p className="text-xs font-medium text-foreground-600 mb-2">六爻记录：</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {record.rounds.map((r, idx) => {
                        const total = r.total;
                        let lineLabel = '';
                        let lineColor = '';
                        if (total === 9) { lineLabel = '老阳'; lineColor = 'text-accent-700'; }
                        else if (total === 7) { lineLabel = '少阳'; lineColor = 'text-accent-600'; }
                        else if (total === 8) { lineLabel = '少阴'; lineColor = 'text-secondary-600'; }
                        else if (total === 6) { lineLabel = '老阴'; lineColor = 'text-primary-600'; }
                        else { lineLabel = total % 2 === 1 ? '阳' : '阴'; lineColor = 'text-foreground-600'; }
                        const changing = total === 6 || total === 9;
                        return (
                          <div key={idx} className="flex items-center justify-between px-2 py-1.5 bg-background-50 rounded-lg border border-background-200 text-xs">
                            <span className="text-foreground-400">第{r.round || idx + 1}爻</span>
                            <span className={`font-medium ${lineColor}`}>{lineLabel}</span>
                            {changing && <span className="text-primary-500 text-xs">变</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => handleViewDetail(record)}
                      className="flex-1 py-2 rounded-lg bg-primary-500 text-background-50 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-all"
                    >
                      查看解读
                    </button>
                    <button
                      onClick={() => handleDelete(record.id)}
                      className="px-4 py-2 rounded-lg bg-background-200 text-foreground-600 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-background-300 transition-all"
                    >
                      删除
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}